/* eslint-disable jsx-a11y/no-noninteractive-tabindex -- The named table scroll region requires keyboard focus. */
import { platformHost } from "../../../../../../functions/platformHost";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import {
  LuCopy,
  LuExternalLink,
  LuPencil,
  LuLockKeyhole,
  LuEye, LuEyeOff, LuX, LuCheck,
} from "react-icons/lu";
import {
  getPlataforms,
  getPlatformAccess,
  platforms,
} from "../../../../../../api/configuration";
import { setAlert } from "../../../../../../reducers/userConfigSlice";
import "./style.css";
const definitions = [
  {
    name: "Tienda Online",
    description: "Escaparate público: catálogo, cesta y pedidos de clientes.",
    enabled: "shop",
    host: "shop",
  },
  {
    name: "Punto de Venta",
    description: "Terminal de mostrador para cobrar y gestionar la caja.",
    key: "tpvPassword",
    enabled: "tpv",
    host: "tpv",
  },
  {
    name: "Gestor de Pedidos",
    description: "Pantalla de preparación y seguimiento de pedidos.",
    key: "kitchenPassword",
    enabled: "kitchen",
    host: "kd",
  },
  {
    name: "Repartidor",
    description: "Acceso del repartidor a los pedidos que tiene asignados.",
    key: "deliveryPassword",
    enabled: "delivery",
    host: "repartidor",
  },
  {
    name: "Admin",
    key: "adminPassword",
    enabled: "admin",
    host: "admin",
    password: true,
  },
];
export const Domains = ({ management = false }) => {
  const dispatch = useDispatch();
  const pending = useRef(false),
    reads = useRef(new Set()),
    generation = useRef(0),
    alive = useRef(true),
    timers = useRef({}),
    focusTarget = useRef(null);
  const [data, setData] = useState(null),
    [values, setValues] = useState({}),
    [editing, setEditing] = useState({}),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [loading, setLoading] = useState(true),
    [retry, setRetry] = useState(0),
    [visibleDraft, setVisibleDraft] = useState({}),
    [revealed, setRevealed] = useState({}),
    [reading, setReading] = useState({}),
    [messages, setMessages] = useState({});
  const hideCodes = useCallback(() => {
    generation.current += 1;
    Object.values(timers.current).forEach(clearTimeout);
    timers.current = {};
    setRevealed({});
    setVisibleDraft({});
  }, []);
  useEffect(() => {
    alive.current = true;
    const onVisibility = () => {
      if (document.hidden) hideCodes();
    };
    window.addEventListener("blur", hideCodes);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      alive.current = false;
      generation.current += 1;
      Object.values(timers.current).forEach(clearTimeout);
      window.removeEventListener("blur", hideCodes);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [hideCodes]);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    getPlataforms()
      .then((response) => {
        if (!active) return;
        if (response?.error !== false || !response.data?.enabled) throw Error();
        setData(response.data);
      })
      .catch(() => {
        if (active)
          setError("No se pudo cargar la configuración de plataformas.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [retry]);
  useEffect(() => {
    if (focusTarget.current) {
      document.getElementById(focusTarget.current)?.focus();
      focusTarget.current = null;
    }
  }, [editing]);
  const toggleCodeEdit = (item) => {
    hideCodes();
    setError("");
    setMessages({});
    focusTarget.current = editing[item.key] ? `${item.key}-edit` : item.key;
    setEditing(previous => ({ ...previous, [item.key]: !previous[item.key] }));
    setValues(previous => ({ ...previous, [item.key]: "" }));
  };
  const notify = (type, message) =>
    dispatch(
      setAlert({
        called: true,
        type,
        title:
          type === "error"
            ? "No se pudieron guardar los accesos"
            : "Accesos de plataforma",
        message,
      }),
    );
  const consult = async (item, copy = false) => {
    const kind = item.enabled;
    if (reads.current.has(kind) || pending.current) return;
    if (!copy && revealed[kind] !== undefined) {
      clearTimeout(timers.current[kind]);
      setRevealed((previous) => {
        const next = { ...previous };
        delete next[kind];
        return next;
      });
      return;
    }
    const sequence = generation.current;
    reads.current.add(kind);
    setReading((previous) => ({ ...previous, [kind]: true }));
    setMessages((previous) => ({ ...previous, [kind]: "" }));
    try {
      const response = await getPlatformAccess(kind);
      if (!alive.current || sequence !== generation.current) return;
      if (
        response?.error !== false ||
        response.data?.platform !== kind ||
        typeof response.data?.code !== "string"
      )
        throw Error();
      const code = response.data.code;
      if (copy) {
        await navigator.clipboard.writeText(code);
        if (!alive.current || sequence !== generation.current) return;
        setMessages((previous) => ({
          ...previous,
          [kind]: "Código guardado copiado.",
        }));
      } else {
        setRevealed((previous) => ({ ...previous, [kind]: code }));
        clearTimeout(timers.current[kind]);
        timers.current[kind] = setTimeout(
          () =>
            setRevealed((previous) => {
              const next = { ...previous };
              delete next[kind];
              return next;
            }),
          15000,
        );
        if (code && !response.data.numeric)
          setMessages((previous) => ({
            ...previous,
            [kind]:
              "Código anterior no numérico. Usa Editar código para guardar uno de seis cifras.",
          }));
      }
    } catch {
      if (alive.current && sequence === generation.current)
        setMessages((previous) => ({
          ...previous,
          [kind]: copy
            ? "No se pudo copiar el código guardado. Reintenta."
            : "No se pudo consultar el código guardado. Reintenta.",
        }));
    } finally {
      reads.current.delete(kind);
      if (alive.current)
        setReading((previous) => ({ ...previous, [kind]: false }));
    }
  };
  const updatePasswords = async (event, onlyKey) => {
    event.preventDefault();
    if (pending.current) return;
    const payload = Object.fromEntries(
      Object.entries(values).filter(([key, value]) => value !== "" && (!onlyKey || onlyKey === key)),
    );
    if (!Object.keys(payload).length) {
      notify("info", "No hay cambios que guardar.");
      return;
    }
    if (Object.entries(payload).some(([key, value]) => key !== "adminPassword" && !/^[0-9]{6}$/.test(value))) {
      setError("Introduce un código de seis cifras. Se conservan los ceros iniciales.");
      return;
    }
    pending.current = true;
    setBusy(true);
    setError("");
    hideCodes();
    setMessages({});
    try {
      const response = await platforms(payload);
      if (response?.error !== false || !response.data?.enabled)
        throw Error(
          response?.message ||
            "No se confirmó el guardado. Conserva los valores y reintenta.",
        );
      if (!alive.current) return;
      setData(response.data);
      if (onlyKey) focusTarget.current = `${onlyKey}-edit`;
      setValues(previous => onlyKey ? { ...previous, [onlyKey]: "" } : {});
      setEditing(previous => onlyKey ? { ...previous, [onlyKey]: false } : {});
      setVisibleDraft({});
      notify(
        "success",
        "Accesos guardados correctamente. Puedes consultar el código actual con Ver.",
      );
    } catch (failure) {
      if (!alive.current) return;
      const message = failure.message || "No se pudo guardar. Reintenta.";
      setError(message);
      notify("error", message);
    } finally {
      pending.current = false;
      if (alive.current) setBusy(false);
    }
  };
  const copyUrl = async (url, kind) => {
    try {
      await navigator.clipboard.writeText(url);
      setMessages((previous) => ({ ...previous, [kind]: "URL copiada." }));
    } catch {
      setMessages((previous) => ({
        ...previous,
        [kind]: "No se pudo copiar la URL.",
      }));
    }
  };
  return (
    <section
      className={
        management
          ? "platformsCompactPanel platformManagement"
          : "platformsCompactPanel"
      }
    >
      {!management && (
        <header className="platformReferenceHeading">
          <h2>Plataformas</h2>
          <p>
            Acceso a cada plataforma del negocio: URL, estado y contraseña
            general.
          </p>
        </header>
      )}
      {loading ? (
        <p role="status">Cargando plataformas…</p>
      ) : !data ? (
        <div role="alert">
          {error}
          <button type="button" onClick={() => setRetry(retry + 1)}>
            Reintentar
          </button>
        </div>
      ) : (
        <form className="platformAccessSettings" onSubmit={management ? updatePasswords : event => event.preventDefault()}>
          {!management && (
            <p className="platformReferenceHint">
              <LuLockKeyhole aria-hidden="true" />
              <span>
                Esta contraseña desbloquea el acceso general a la plataforma.
                Después, cada usuario accede con su PIN personal.
              </span>
            </p>
          )}
          {error && <p role="alert">{error}</p>}
          <div
            className="platformAccessScroll"
            role="region"
            aria-label="Plataformas del negocio"
            tabIndex={0}
          >
            <table className="platformAccessTable">
              <caption className="visually-hidden">
                Plataformas, URL, estado y accesos del negocio
              </caption>
              <thead>
                <tr>
                  {["Plataforma", "URL", "Estado", "Contraseña"].map(
                    (label) => (
                      <th key={label} scope="col">
                        {label}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody>
                {definitions
                  .filter((item) => management || !item.password)
                  .map((item) => {
                    const enabled = data.enabled[item.enabled] === true,
                      host = platformHost(data.subdomain || "", item.host),
                      url = `https://${host}`,
                      hasCode = data.accessConfigured?.[item.enabled] !== false,
                      shown = revealed[item.enabled] !== undefined;
                    return (
                      <tr key={item.enabled}>
                        <th scope="row">
                          <strong>{item.name}</strong>
                          {item.description && (
                            <small>{item.description}</small>
                          )}
                        </th>
                        <td>
                          <div className="platformUrl">
                            <a
                              href={url}
                              title={url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {url}
                            </a>
                            <button
                              type="button"
                              aria-label={`Copiar URL de ${item.name}`}
                              onClick={() => copyUrl(url, item.enabled)}
                            >
                              <LuCopy aria-hidden="true" />
                            </button>
                            <a
                              className="platformOpen"
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              aria-label={`Abrir ${item.name}`}
                            >
                              <LuExternalLink aria-hidden="true" />
                            </a>
                          </div>
                        </td>
                        <td>
                          <span
                            className={`platformState ${enabled ? "is-active" : "is-inactive"}`}
                          >
                            {enabled ? "Activa" : "Desactivada"}
                          </span>
                        </td>
                        <td>
                          {!item.key ? (
                            <span className="platformAccessMuted">
                              Sin contraseña de plataforma
                            </span>
                          ) : !enabled ? (
                            <span className="platformAccessMuted">
                              Plataforma desactivada
                            </span>
                          ) : !item.password ? (
                            <div className="platformOperationalAccess">
                              {editing[item.key] ? (
                                <>
                                  <input
                                    id={item.key}
                                    type="text"
                                    inputMode="numeric"
                                    autoComplete="off"
                                    maxLength={6}
                                    pattern="[0-9]{6}"
                                    placeholder="6 cifras"
                                    aria-label={`Nuevo código de ${item.name}`}
                                    aria-describedby={`${item.key}-digits`}
                                    value={values[item.key] || ""}
                                    disabled={busy}
                                    onChange={event => {
                                      const value = event.target.value;
                                      if (!/^[0-9]*$/.test(value)) {
                                        setError("Los códigos de plataforma solo admiten números.");
                                        return;
                                      }
                                      setError("");
                                      setValues(previous => ({ ...previous, [item.key]: value }));
                                    }}
                                    onKeyDown={event => {
                                      if (event.key === "Escape") { event.preventDefault(); toggleCodeEdit(item); }
                                      if (event.key === "Enter") { event.preventDefault(); void updatePasswords(event, item.key); }
                                    }}
                                  />
                                  <span className="visually-hidden" id={`${item.key}-digits`}>Seis cifras numéricas. Se conservan los ceros iniciales.</span>
                                  <div className="platformCodeIcons">
                                    <button type="button" disabled={busy} aria-label={`Cancelar cambio de ${item.name}`} title="Cancelar" onClick={() => toggleCodeEdit(item)}><LuX aria-hidden="true" /></button>
                                    <button type="button" disabled={busy || !/^[0-9]{6}$/.test(values[item.key] || "")} aria-label={`Guardar código de ${item.name}`} title="Guardar código" onClick={event => updatePasswords(event, item.key)}><LuCheck aria-hidden="true" /></button>
                                  </div>
                                </>
                              ) : (
                                <>
                                  <div className="platformStoredValue" aria-label={`Acceso guardado de ${item.name}`}>
                                    {shown ? <code>{revealed[item.enabled] || "Sin código configurado"}</code> : hasCode ? <span aria-label="Código guardado oculto">••••••</span> : <span>Sin código configurado</span>}
                                  </div>
                                  <div className="platformCodeIcons">
                                    <button id={`${item.key}-edit`} type="button" disabled={busy} aria-label={`Cambiar acceso de ${item.name}`} title="Editar código" onClick={() => toggleCodeEdit(item)}><LuPencil aria-hidden="true" /></button>
                                    {shown && hasCode && <button type="button" disabled={busy || reading[item.enabled]} aria-label={`Copiar código guardado de ${item.name}`} title="Copiar código" onClick={() => consult(item, true)}><LuCopy aria-hidden="true" /></button>}
                                    {hasCode && <button type="button" disabled={busy || reading[item.enabled]} aria-label={`${shown ? "Ocultar" : "Ver"} código guardado de ${item.name}`} aria-pressed={shown} aria-busy={reading[item.enabled] || false} title={shown ? "Ocultar código" : "Mostrar código"} onClick={() => consult(item)}>{shown ? <LuEyeOff aria-hidden="true" /> : <LuEye aria-hidden="true" />}</button>}
                                  </div>
                                </>
                              )}
                            </div>
                          ) : (
                            <>
                              <span className="platformAccessMuted">{hasCode ? "Contraseña configurada" : "Sin contraseña configurada"}</span>
                              <div className="platformAccessActions">
                                <button id={`${item.key}-edit`} type="button" disabled={busy} aria-expanded={Boolean(editing[item.key])} aria-label={`${editing[item.key] ? "Cancelar cambio" : "Cambiar acceso"} de ${item.name}`} onClick={() => toggleCodeEdit(item)}>
                                  {editing[item.key] ? "Cancelar" : <><LuPencil aria-hidden="true" /><span>Cambiar</span></>}
                                </button>
                              </div>
                              {editing[item.key] && (
                                <div className="platformAccessField">
                                  <label htmlFor={item.key}>Nueva contraseña de plataforma Admin</label>
                                  <div>
                                    <input id={item.key} type={visibleDraft[item.key] ? "text" : "password"} autoComplete="new-password" maxLength={128} value={values[item.key] || ""} disabled={busy} placeholder="Sin cambios" onChange={event => { setError(""); setValues(previous => ({ ...previous, [item.key]: event.target.value })); }} aria-describedby={`${item.key}-help`} />
                                    <button type="button" aria-label={`${visibleDraft[item.key] ? "Ocultar" : "Mostrar"} nuevo acceso de ${item.name}`} disabled={busy} onClick={() => setVisibleDraft(previous => ({ ...previous, [item.key]: !previous[item.key] }))}>{visibleDraft[item.key] ? "Ocultar" : "Mostrar"}</button>
                                  </div>
                                  <small id={`${item.key}-help`}>Mínimo 8 caracteres con mayúscula, minúscula, número y símbolo. La contraseña de Admin no se puede recuperar en texto.</small>
                                </div>
                              )}
                            </>
                          )}
                          {messages[item.enabled] && (
                            <p className="platformAccessMessage" role="status">
                              {messages[item.enabled]}
                            </p>
                          )}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
          {management && (
            <div className="platformAccessSave">
              <button type="submit" disabled={busy}>
                {busy ? "Guardando…" : "Guardar accesos"}
              </button>
            </div>
          )}
        </form>
      )}
    </section>
  );
};
